import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';

@Component({
  selector: 'aid-form-actions',
  template: `
    <div class="container">
      <button
        [disabled]="loading || rightActionInProgress"
        mat-flat-button
        class="action__cancel"
        (click)="onClickLeft()"
        type="button"
      >
        {{ buttonLeft | translate | sentancecase }}
      </button>
      <button
        mat-flat-button
        [color]="buttonRightColor"
        class="action__active"
        [disabled]="rightActionDisabled"
        [ngClass]="{ action__disabled: rightActionDisabled }"
        type="button"
        (click)="onClickRight()"
        data-cy="customer-save"
      >
        <div class="action__progress">
          @if (loading) {
            <mat-progress-spinner
              mode="indeterminate"
              [diameter]="24"
              [strokeWidth]="3"
            >
            </mat-progress-spinner>
          }
        </div>

        @if (!loading) {
          <span>
            {{ buttonRight | translate | sentancecase }}
          </span>
        }
      </button>
    </div>
  `,
  styleUrls: ['./form-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class FormActionsComponent {
  @Input() buttonLeft = 'cancel';
  @Input() buttonRight = 'save';
  @Input() disable = false;
  @Input() buttonRightColor: ThemePalette = 'primary';

  private _loading = false;

  @Input()
  set loading(value: boolean) {
    this._loading = value;
    if (!value) {
      this.rightActionInProgress = false;
    }
  }

  get loading(): boolean {
    return this._loading;
  }

  @Input() form: AbstractControl | null = null;

  @Output() clickLeft = new EventEmitter();
  @Output() clickRight = new EventEmitter();

  /**
   * Protects the synchronous interval between a click and the parent's next
   * change-detection pass. The parent remains the owner of loading.
   */
  rightActionInProgress = false;

  get rightActionDisabled(): boolean {
    return (
      this.disable ||
      this.loading ||
      this.rightActionInProgress ||
      this.form?.invalid === true ||
      this.form?.pending === true
    );
  }

  onClickLeft() {
    if (!this.loading && !this.rightActionInProgress) {
      this.clickLeft.emit();
    }
  }

  onClickRight() {
    if (this.rightActionDisabled) {
      return;
    }

    this.rightActionInProgress = true;
    try {
      this.clickRight.emit();
    } catch (error) {
      this.rightActionInProgress = false;
      throw error;
    }

    // Local actions can close the view without owning a loading input. Remote
    // owners set loading synchronously before returning from this event.
    Promise.resolve().then(() => {
      this.rightActionInProgress = false;
    });
  }
}
